import Link from 'next/link';
import { FC } from 'react';
import { BsArrowLeftRight } from 'react-icons/bs';
import { IoBriefcaseOutline, IoCopyOutline, IoPeopleOutline } from 'react-icons/io5';
import { TbPointFilled } from 'react-icons/tb';

import { Badge, Box, BoxProps, Button, Group, Modal, Space, Stack, Text } from '@mantine/core';
import { useDisclosure, useIntersection } from '@mantine/hooks';
import useSWRImmutable from 'swr/immutable';

import { PermissionProject, Project, ProjectTeaser as ProjectTeaserType } from 'models';
import { TeamHelpers } from 'helpers/TeamHelpers';

import { SettingsDropdown } from 'components/dropdown/SettingsDropdown';
import { ProjectMetaItem } from 'components/project-overview/components/ProjectList';
import { useProjectMeta } from 'components/project-overview/hooks';
import { useProjectListsContext } from 'components/project-overview/hooks/use-project-lists-context';
import { ProjectTeaserUpdateDot } from 'components/project-teaser/ProjectTeaserUpdateDot';
import { useAction } from 'hooks/use-action';
import { useCurrentTeams } from 'hooks/use-current-teams';
import { useCurrentUser } from 'hooks/use-current-user';
import { useTeam } from 'hooks/use-team';
import { LocalNotificationService } from 'services/LocalNotificationService';
import { ProjectService } from 'services/ProjectService';

import { ProjectTeaserFollow } from './ProjectTeaserFollow';
import { ProjectTeaserUpdated } from './ProjectTeaserUpdated';

import cx from './ProjectTeaser.module.scss';

export const ProjectTeaser: FC<
    {
        project: Project | ProjectTeaserType;
    } & BoxProps
> = ({ project, ...boxProps }) => {
    const user = useCurrentUser();
    const teams = useCurrentTeams();
    const { team } = useTeam(project.team || undefined);
    const { projectUrl, teamName } = useProjectMeta(project);

    const canMoveTeam = TeamHelpers.canMoveProject(team, user, teams);
    const canDelete = TeamHelpers.hasUserPermission(team, user, PermissionProject.DELETE);

    const { ref: intersectionRef, entry } = useIntersection({ threshold: 0.1 });
    const teaserIsVisible = entry?.isIntersecting;

    const { data: teaserData } = useSWRImmutable(
        user && teaserIsVisible ? `/api/projects/${project.id}/teaser` : null,
        () => ProjectService.getTeaser(project.id),
        { keepPreviousData: true },
    );

    return (
        <Box ref={intersectionRef} className={cx.wrapper} component={Link} href={projectUrl} {...boxProps}>
            <Stack className={cx.details} gap={6}>
                <Text size="lg" fw={600} lh={1.2}>
                    {project.name} <ProjectTeaserUpdateDot project={project} lastView={teaserData?.lastView} />
                </Text>

                {project.customer && (
                    <ProjectMetaItem
                        label={`Customer: ${project.customer}`}
                        icon={<IoBriefcaseOutline size={12} />}
                        c="gray.6"
                    />
                )}

                {teamName && (
                    <ProjectMetaItem label={`Team: ${teamName}`} icon={<IoPeopleOutline size={12} />} c="gray.6" />
                )}

                <ProjectTeaserUpdated project={project} />

                <Group gap={4} data-hidden-if-empty pt="xs" mt="auto">
                    {!!teaserData?.nbUnreadMessages && (
                        <Badge size="xs" color="red" variant="light" leftSection={<TbPointFilled size={10} />}>
                            {teaserData?.nbUnreadMessages} new{' '}
                            {teaserData?.nbUnreadMessages === 1 ? 'message' : 'messages'}
                        </Badge>
                    )}
                </Group>
            </Stack>

            <Group data-project-action gap={4}>
                <Box onClick={(e) => e.preventDefault()}>
                    <SettingsDropdown>
                        <DuplicateButton project={project} />
                        {canMoveTeam && <MoveToTeamButton project={project} />}
                        {canDelete && <DeleteButton project={project} />}
                    </SettingsDropdown>
                </Box>
                <ProjectTeaserFollow projectId={project.id} team={team} isVisible={!!teaserIsVisible} />
            </Group>
        </Box>
    );
};

export const DuplicateButton = ({ project }: { project: Project | ProjectTeaserType }) => {
    const handleDuplicate = () => {
        ProjectService.navigate.duplicate(project.id, project.name, project.isReferenceDesign).then();
    };

    return (
        <SettingsDropdown.Item onClick={handleDuplicate} leftSection={<IoCopyOutline />}>
            Duplicate project
        </SettingsDropdown.Item>
    );
};

export const MoveToTeamButton = ({ project }: { project: Project | ProjectTeaserType }) => {
    const handleMoveToTeam = () => {
        ProjectService.navigate.moveTeam(project.id).then();
    };

    return (
        <SettingsDropdown.Item onClick={handleMoveToTeam} leftSection={<BsArrowLeftRight />}>
            Move project to other team
        </SettingsDropdown.Item>
    );
};

export const DeleteButton = ({ project }: { project: Project | ProjectTeaserType }) => {
    const [opened, handlers] = useDisclosure();
    const { refresh: refreshProjectLists } = useProjectListsContext();

    const [handleDelete, loading] = useAction(async () => {
        try {
            await ProjectService.delete(project.id);

            refreshProjectLists();

            LocalNotificationService.showSuccess({ message: 'Project deleted' });
        } catch (error) {
            LocalNotificationService.showError({ message: "Couldn't delete project" });
            console.error(`Error deleting project: ${project.name} (ID: ${project.id})`, error);
        }

        handlers.close();
    });

    return (
        <>
            <SettingsDropdown.Delete onClick={handlers.open} />
            <Modal opened={opened} onClose={handlers.close} title="Confirm delete project">
                <Text>
                    Are you sure you want to delete{' '}
                    <Text span fw={600}>
                        {project.name}
                    </Text>{' '}
                    and all its designs and diagrams?
                </Text>

                <Space h="md" />

                <Group gap={4}>
                    <Button variant="light" color="red" onClick={handleDelete} loading={loading}>
                        Yes, delete
                    </Button>
                    <Button variant="subtle" onClick={handlers.close}>
                        Cancel
                    </Button>
                </Group>
            </Modal>
        </>
    );
};
